// Routine schedules in plain English, and when they fire next.
//
// A routine's `schedule_display` (backend openclaw_cron) is one of:
//   - a 5-field cron expression   "25 10,16 * * 1-5"
//   - an interval                 "every 20m", "every 1h30m", "every 1d"
//   - a one-shot ISO instant      "2026-09-24T09:00:00.000Z" (repeat_display "once")
//
// Cron hours are printed as written: they are evaluated in the routine's
// time zone (Europe/London by default), which the API does not return,
// so for a UK viewer they are local. One-shot instants ARE UTC and are
// converted to local time. Anything we cannot state exactly comes back
// as null (humanize*) or as the raw string (formatSchedule): a raw cron
// with a tooltip is better than a wrong sentence.
//
// No `@/` imports so the self-test bundles standalone.

import {
  daysBetween,
  formatDate,
  formatTime,
  joinList,
  MONTHS,
  toDate,
  WEEKDAYS,
  WEEKDAYS_LONG,
  type DateInput,
} from "./format";

export interface CronSpec {
  minutes: number[];
  hours: number[];
  dom: number[];
  months: number[];
  /** 0 = Sunday … 6 = Saturday (7 is folded into 0). */
  dow: number[];
  /** Field text starts with "*" (Vixie cron: then dom AND dow must match, else OR). */
  domStar: boolean;
  dowStar: boolean;
  raw: string;
}

export interface ScheduleLike {
  schedule_display: string | null;
  repeat_display?: string | null;
  next_run_at?: string | null;
  last_run_at?: string | null;
}

export interface FormattedSchedule {
  /** Plain English, or `raw` itself when the shape is not understood. */
  text: string;
  /** The string the API sent (for a tooltip). */
  raw: string;
  /** Runs once (ISO instant or repeat_display "once"). */
  oneOff: boolean;
}

const MACROS: Record<string, string> = {
  "@yearly": "0 0 1 1 *", "@annually": "0 0 1 1 *", "@monthly": "0 0 1 * *",
  "@weekly": "0 0 * * 0", "@daily": "0 0 * * *", "@midnight": "0 0 * * *", "@hourly": "0 * * * *",
};
const MONTH_NAMES = MONTHS.map((m) => m.toLowerCase());
const DOW_NAMES = WEEKDAYS.map((d) => d.toLowerCase());

interface FieldDef {
  min: number;
  max: number;
  names?: string[];
  nameBase?: number;
}
const FIELDS: FieldDef[] = [
  { min: 0, max: 59 },
  { min: 0, max: 23 },
  { min: 1, max: 31 },
  { min: 1, max: 12, names: MONTH_NAMES, nameBase: 1 },
  { min: 0, max: 7, names: DOW_NAMES, nameBase: 0 },
];

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function fieldValue(token: string, def: FieldDef): number | null {
  if (/^\d+$/.test(token)) return Number(token);
  const idx = def.names?.indexOf(token.toLowerCase()) ?? -1;
  return idx >= 0 ? idx + (def.nameBase ?? 0) : null;
}

function parseField(text: string, def: FieldDef, isDow: boolean): number[] | null {
  const out = new Set<number>();
  for (const part of text.split(",")) {
    const m = /^(\*|[a-z0-9]+(?:-[a-z0-9]+)?)(?:\/(\d+))?$/i.exec(part);
    if (!m) return null;
    const step = m[2] ? Number(m[2]) : 1;
    if (step < 1) return null;
    let lo: number | null;
    let hi: number | null;
    if (m[1] === "*") {
      lo = def.min;
      hi = isDow ? 6 : def.max;
    } else {
      const [a, b] = m[1].split("-");
      lo = fieldValue(a, def);
      hi = b !== undefined ? fieldValue(b, def) : m[2] ? def.max : lo;
    }
    if (lo === null || hi === null || lo < def.min || hi > def.max || lo > hi) return null;
    for (let v = lo; v <= hi; v += step) out.add(isDow && v === 7 ? 0 : v);
  }
  return out.size ? [...out].sort((a, b) => a - b) : null;
}

/** Five-field cron (plus @daily-style macros) → sets of values, or null. */
export function parseCron(expr: string | null | undefined): CronSpec | null {
  const raw = (expr ?? "").trim();
  const text = MACROS[raw.toLowerCase()] ?? raw;
  const parts = text.split(/\s+/);
  if (parts.length !== 5) return null;
  const sets = parts.map((p, i) => parseField(p, FIELDS[i], i === 4));
  if (sets.some((s) => s === null)) return null;
  const [minutes, hours, dom, months, dow] = sets as number[][];
  return { minutes, hours, dom, months, dow, domStar: parts[2].startsWith("*"), dowStar: parts[4].startsWith("*"), raw };
}

// ─── intervals ───

const UNIT_SECONDS: Record<string, number> = { d: 86_400, h: 3600, m: 60, s: 1 };

/** "every 20m" / "1h30m" / "every 2 hours" → seconds (and how many parts it had). */
export function parseInterval(s: string | null | undefined): { seconds: number; parts: number } | null {
  const text = (s ?? "").trim().toLowerCase().replace(/^every\s+/, "");
  if (!text) return null;
  // Longest unit spellings first, or "2 hours" would stop after the "h".
  const re = /(\d+)\s*(days?|d|hours?|hrs?|h|minutes?|mins?|m|seconds?|secs?|s)\s*/gy;
  let seconds = 0;
  let parts = 0;
  let end = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    seconds += Number(m[1]) * UNIT_SECONDS[m[2][0]];
    parts += 1;
    end = re.lastIndex;
  }
  return parts && end === text.length && seconds > 0 ? { seconds, parts } : null;
}

function exactDuration(total: number): string {
  const units: [number, string][] = [[86_400, "d"], [3600, "h"], [60, "min"], [1, "s"]];
  const out: string[] = [];
  let rest = total;
  for (const [size, label] of units) {
    if (rest >= size) {
      out.push(`${Math.floor(rest / size)} ${label}`);
      rest %= size;
    }
  }
  return out.join(" ");
}

/** "every 20m" → "Every 20 min", "every 1h" → "Hourly", "every 1h30m" → "Every 1 h 30 min". */
export function humanizeInterval(s: string | null | undefined): string | null {
  const parsed = parseInterval(s);
  if (!parsed) return null;
  const { seconds, parts } = parsed;
  if (seconds % 86_400 === 0) return seconds === 86_400 ? "Every day" : `Every ${seconds / 86_400} days`;
  if (seconds % 3600 === 0) return seconds === 3600 ? "Hourly" : `Every ${seconds / 3600} hours`;
  if (parts === 1 && seconds % 60 === 0) return seconds === 60 ? "Every minute" : `Every ${seconds / 60} min`;
  if (parts === 1) return `Every ${seconds} s`;
  return `Every ${exactDuration(seconds)}`;
}

// ─── cron → English ───

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

function hm(h: number, m: number): string {
  return `${pad2(h)}:${pad2(m)}`;
}

/** Constant gap between sorted values, or 0 when uneven. */
function evenStep(values: number[]): number {
  if (values.length < 2) return 0;
  const step = values[1] - values[0];
  return values.every((v, i) => i === 0 || v - values[i - 1] === step) ? step : 0;
}

const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];

function dowPhrase(dow: number[]): string {
  const key = dow.join(",");
  if (key === "1,2,3,4,5") return "Weekdays";
  if (key === "0,6") return "Weekends";
  if (dow.length === 1) return `${WEEKDAYS_LONG[dow[0]]}s`;
  return joinList(MONDAY_FIRST.filter((d) => dow.includes(d)).map((d) => WEEKDAYS[d]));
}

type DayPart = { kind: "daily" } | { kind: "prefix"; text: string } | { kind: "suffix"; text: string };

const MONTH_DAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// One date a year ("45 9-19 24 9 *"). Agents use it as a one-day
// schedule, so within the coming week it reads "today only" /
// "tomorrow only" / "24 Sep only"; otherwise it is what cron does:
// every year. @yearly is always "every year".
function yearlyPart(month: number, day: number, now: Date, macro: boolean): DayPart | null {
  if (day > MONTH_DAYS[month]) return null; // 31 Feb, 31 Apr: never fires
  const every: DayPart = { kind: "prefix", text: `Every year on ${day} ${MONTHS[month]}` };
  if (macro) return every;
  // The next time this date comes round (29 Feb only in a leap year).
  let next: Date | null = null;
  for (let y = now.getFullYear(); y <= now.getFullYear() + 8 && !next; y += 1) {
    const d = new Date(y, month, day);
    if (d.getMonth() === month && daysBetween(now, d) >= 0) next = d;
  }
  const ahead = next ? daysBetween(now, next) : Infinity;
  if (ahead === 0) return { kind: "suffix", text: "today only" };
  if (ahead === 1) return { kind: "suffix", text: "tomorrow only" };
  if (ahead <= 7) return { kind: "suffix", text: `${day} ${MONTHS[month]} only` };
  return month === 1 && day === 29 ? null : every;
}

// The shortest month in a list (February as 28: a 29th is not every year).
function shortestMonth(months: number[]): number {
  return Math.min(...months.map((m) => (m === 2 ? 28 : MONTH_DAYS[m - 1])));
}

// Some days of some months: "Quarterly on the 20th" (every third month
// through the year), else "On the 20th of Mar and Sep". A day not in every
// listed month (the 31st of Sep) is not one sentence: null.
function monthListPart(dom: number[], months: number[]): DayPart | null {
  if (dom.length > 3 || dom[dom.length - 1] > shortestMonth(months)) return null;
  const days = joinList(dom.map(ordinal));
  if (months.length === 4 && evenStep(months) === 3) return { kind: "prefix", text: `Quarterly on the ${days}` };
  return { kind: "prefix", text: `On the ${days} of ${joinList(months.map((m) => MONTHS[m - 1]))}` };
}

function dayPart(spec: CronSpec, now: Date): DayPart | null {
  const domAll = spec.dom.length === 31;
  const dowAll = spec.dow.length === 7;
  const monAll = spec.months.length === 12;
  // "*/2" in a day or month field restricts days but reads as a star to cron.
  if ((spec.domStar && !domAll) || (spec.dowStar && !dowAll)) return null;
  if (!domAll && !dowAll) return null; // cron ORs them: not one sentence
  if (!dowAll) return monAll ? { kind: "prefix", text: dowPhrase(spec.dow) } : null;
  if (!domAll) {
    if (monAll) return spec.dom.length <= 3 ? { kind: "prefix", text: `Monthly on the ${joinList(spec.dom.map(ordinal))}` } : null;
    if (spec.months.length === 1 && spec.dom.length === 1) return yearlyPart(spec.months[0] - 1, spec.dom[0], now, /^@(yearly|annually)$/i.test(spec.raw));
    return monthListPart(spec.dom, spec.months);
  }
  return monAll ? { kind: "daily" } : null;
}

type TimePart = { kind: "at"; times: string[] } | { kind: "interval"; text: string };

function timePart(minutes: number[], hours: number[]): TimePart | null {
  const mStep = evenStep(minutes);
  // Every N minutes through the whole hour, starting on the hour (or at :05…).
  const coversHour = minutes.length === 1 ? false : mStep > 0 && 60 % mStep === 0 && minutes.length * mStep === 60;
  if (hours.length === 24) {
    if (minutes.length === 60) return { kind: "interval", text: "every minute" };
    if (minutes.length === 1) return { kind: "interval", text: minutes[0] === 0 ? "hourly" : `hourly at :${pad2(minutes[0])}` };
    if (coversHour) return { kind: "interval", text: `every ${mStep} min${minutes[0] ? ` from :${pad2(minutes[0])}` : ""}` };
    if (minutes.length <= 4) return { kind: "interval", text: `hourly at ${joinList(minutes.map((m) => `:${pad2(m)}`))}` };
    return null;
  }
  const first = hours[0];
  const last = hours[hours.length - 1];
  const hStep = evenStep(hours);
  if (minutes.length === 1) {
    const m = minutes[0];
    if (hours.length === 1) return { kind: "at", times: [hm(first, m)] };
    const span = `${hm(first, m)}–${hm(last, m)}`;
    if (hStep === 1 && hours.length >= 3) return { kind: "interval", text: m === 0 ? `hourly ${span}` : `hourly at :${pad2(m)}, ${span}` };
    if (hStep >= 2 && hours.length >= 4) return { kind: "interval", text: `every ${hStep} hours, ${span}` };
    if (hours.length <= 6) return { kind: "at", times: hours.map((h) => hm(h, m)) };
    return { kind: "interval", text: `${hours.length} times a day, ${span}` };
  }
  if (coversHour && hStep === 1) {
    return { kind: "interval", text: `every ${mStep} min, ${hm(first, minutes[0])}–${hm(last, minutes[minutes.length - 1])}` };
  }
  if (minutes.length * hours.length <= 6) {
    return { kind: "at", times: hours.flatMap((h) => minutes.map((m) => hm(h, m))) };
  }
  return null;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * "25 10,16 * * 1-5" → "Weekdays at 10:25 and 16:25", "0 10 20 3,6,9,12 *"
 * → "Quarterly on the 20th at 10:00"; null for any shape it cannot state
 * exactly (dom and dow both set, every day of some months, steps on day
 * fields, 6-field cron, junk).
 */
export function humanizeCron(expr: string | null | undefined, now?: DateInput): string | null {
  const spec = parseCron(expr);
  if (!spec) return null;
  const day = dayPart(spec, toDate(now) ?? new Date());
  const time = timePart(spec.minutes, spec.hours);
  if (!day || !time) return null;
  if (time.kind === "at") {
    const at = joinList(time.times);
    if (day.kind === "daily") return `Daily at ${at}`;
    if (day.kind === "prefix") return `${day.text} at ${at}`;
    return `At ${at}, ${day.text}`;
  }
  if (day.kind === "daily") return capitalize(time.text);
  if (day.kind === "prefix") return `${day.text}, ${time.text}`;
  return `${capitalize(time.text)}, ${day.text}`;
}

// ─── one-shots and the combined formatter ───

/** The instant of a one-shot schedule ("2026-09-24T09:00:00.000Z" or "at …"), else null. */
export function oneShotDate(raw: string | null | undefined): Date | null {
  const text = (raw ?? "").trim().replace(/^at\s+/i, "");
  if (!/^\d{4}-\d{2}-\d{2}(?:$|[T ]\d{2}:\d{2})/.test(text)) return null;
  return toDate(text);
}

function onceText(d: Date, now: Date): string {
  return `Once, ${formatDate(d, {}, now)} at ${formatTime(d)}`;
}

/**
 * A routine's schedule for display: {text, raw, oneOff}. `text === raw`
 * means the shape was not understood - show it raw with a tooltip.
 */
export function formatSchedule(row: ScheduleLike, now?: DateInput): FormattedSchedule {
  const n = toDate(now) ?? new Date();
  const raw = (row.schedule_display ?? "").trim();
  const once = (row.repeat_display ?? "").trim().toLowerCase() === "once";
  const shot = oneShotDate(raw);
  if (shot) return { text: onceText(shot, n), raw, oneOff: true };
  if (!raw || raw.toLowerCase() === "once") {
    const next = toDate(row.next_run_at);
    if (next) return { text: onceText(next, n), raw, oneOff: true };
    return { text: raw ? "Once" : "", raw, oneOff: once || !!raw };
  }
  const every = humanizeInterval(raw);
  if (every) return { text: every, raw, oneOff: false };
  if (once) {
    // cron + repeatTimes=1: the next fire IS the one run.
    const when = toDate(row.next_run_at) ?? nextRun(raw, n);
    return when ? { text: onceText(when, n), raw, oneOff: true } : { text: raw, raw, oneOff: true };
  }
  return { text: humanizeCron(raw, n) ?? raw, raw, oneOff: false };
}

/** Shorthand for formatSchedule({schedule_display}).text. */
export function describeSchedule(display: string | null | undefined, now?: DateInput): string {
  return formatSchedule({ schedule_display: display ?? null }, now).text;
}

// ─── next fire ───

function dayMatches(spec: CronSpec, d: Date): boolean {
  if (!spec.months.includes(d.getMonth() + 1)) return false;
  const dom = spec.dom.includes(d.getDate());
  const dow = spec.dow.includes(d.getDay());
  return spec.domStar || spec.dowStar ? dom && dow : dom || dow;
}

function nextCronFire(spec: CronSpec, from: Date, until?: Date): Date | null {
  // Fires at whole minutes; a fire exactly at `from` counts.
  const start = new Date(from.getTime());
  if (start.getSeconds() || start.getMilliseconds()) start.setSeconds(60, 0);
  // Five years covers a 29 Feb schedule; `until` stops the scan early.
  for (let i = 0; i < 366 * 5; i += 1) {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    if (until && day.getTime() >= until.getTime()) return null;
    if (!dayMatches(spec, day)) continue;
    for (const h of spec.hours) {
      for (const m of spec.minutes) {
        const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, m);
        if (at.getTime() >= start.getTime()) return at;
      }
    }
  }
  return null;
}

/**
 * Next time a schedule fires at or after `from` (default now), or null.
 * Cron is evaluated in local time; intervals need an anchor (the row's
 * next_run_at or last_run_at); one-shots return their instant if still
 * ahead.
 */
export function nextRun(schedule: string | ScheduleLike | null | undefined, from?: DateInput): Date | null {
  if (!schedule) return null;
  const row: ScheduleLike = typeof schedule === "string" ? { schedule_display: schedule } : schedule;
  const f = toDate(from) ?? new Date();
  const raw = (row.schedule_display ?? "").trim();
  const shot = oneShotDate(raw);
  if (shot) return shot.getTime() >= f.getTime() ? shot : null;
  const interval = parseInterval(raw);
  if (interval) {
    const step = interval.seconds * 1000;
    const next = toDate(row.next_run_at);
    if (next && next.getTime() >= f.getTime()) return next;
    const anchor = next ?? toDate(row.last_run_at);
    if (!anchor) return null;
    const behind = Math.max(0, f.getTime() - anchor.getTime());
    return new Date(anchor.getTime() + Math.ceil(behind / step) * step);
  }
  const spec = parseCron(raw);
  if (spec) return nextCronFire(spec, f);
  const next = toDate(row.next_run_at);
  return next && next.getTime() >= f.getTime() ? next : null;
}

/** Up to `n` fires at or after `from` (calendar views, previews). */
export function nextFires(schedule: string | ScheduleLike | null | undefined, from: DateInput, n: number): Date[] {
  if (!schedule) return [];
  const row: ScheduleLike = typeof schedule === "string" ? { schedule_display: schedule } : schedule;
  const raw = (row.schedule_display ?? "").trim();
  const limit = Math.min(Math.max(0, n), 500);
  const first = nextRun(row, from);
  if (!first || limit === 0) return [];
  const out = [first];
  const spec = parseCron(raw);
  const interval = spec ? null : parseInterval(raw);
  // One-shots (and rows we only know next_run_at for) fire once.
  while (out.length < limit && (spec || interval)) {
    const prev = out[out.length - 1];
    const next = spec
      ? nextCronFire(spec, new Date(prev.getTime() + 60_000))
      : new Date(prev.getTime() + (interval?.seconds ?? 0) * 1000);
    if (!next) break;
    out.push(next);
  }
  return out;
}

/**
 * Fires in [start, end) - e.g. one calendar day - stopping at `end`.
 * `max` is only a safety limit; `truncated` says it was hit.
 */
export function firesInRange(
  schedule: string | ScheduleLike | null | undefined,
  start: DateInput,
  end: DateInput,
  max = 2000,
): { fires: Date[]; truncated: boolean } {
  const from = toDate(start);
  const until = toDate(end);
  const fires: Date[] = [];
  if (!schedule || !from || !until || until.getTime() <= from.getTime()) return { fires, truncated: false };
  const row: ScheduleLike = typeof schedule === "string" ? { schedule_display: schedule } : schedule;
  const raw = (row.schedule_display ?? "").trim();
  const spec = parseCron(raw);
  const interval = spec ? null : parseInterval(raw);
  let next = spec ? nextCronFire(spec, from, until) : nextRun(row, from);
  while (next && next.getTime() < until.getTime()) {
    if (fires.length >= max) return { fires, truncated: true };
    fires.push(next);
    if (spec) next = nextCronFire(spec, new Date(next.getTime() + 60_000), until);
    else if (interval) next = new Date(next.getTime() + interval.seconds * 1000);
    else break; // a one-shot fires once
  }
  return { fires, truncated: false };
}

/** Fires in [start, end); see firesInRange. */
export function firesBetween(schedule: string | ScheduleLike | null | undefined, start: DateInput, end: DateInput, max = 2000): Date[] {
  return firesInRange(schedule, start, end, max).fires;
}
